import {
  Menu,
  MenuButton,
  IconButton,
  Portal,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { useProjectModal } from '$/pages/DashboardPages/pages/Projects/hooks/useProjectModal';
import { duplicateProject } from '$/services/usecases/projects';
import { type ProjectOverviewItem } from '$/services/usecases/projects/mapper/projectOverview';
import { useProjectStore } from '$/stores/useProjectStore';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  project: ProjectOverviewItem;
}

export const ProjectCardContextMenu: FC<Props> = ({
  isOpen,
  onClose,
  onOpen,
  project,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onOpenModal = useProjectModal.useOnOpen();
  const refetchProjects = useProjectStore.useRefetchProjects();

  return (
    <Menu isOpen={isOpen} onClose={onClose} placement='bottom-end'>
      <MenuButton
        as={IconButton}
        layerStyle='floatCardButton'
        icon={<Icon boxSize='15px' color='text' icon='three_dots' />}
        onClick={(event) => {
          onOpen();
          event.preventDefault();
          event.stopPropagation();
        }}
        variant='text'
      />
      <Portal>
        <MenuList fontSize='sm'>
          <MenuItem
            onClick={async (event) => {
              event.stopPropagation();
              let route: string;
              if (project.type === 'editor') {
                route = `/editor?projectId=${project.id}`;
              } else if (project.type === 'legacy-inspiration') {
                route = `/inspirations/legacy/${project.id}`;
              } else {
                route = `/inspiration-project/${project.id}`;
              }
              await navigate({ to: route });
            }}
          >
            {t('dashboard.projects.open')}
          </MenuItem>
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              onOpenModal('renameProject', { project });
            }}
          >
            {t('dashboard.projects.rename')}
          </MenuItem>
          <MenuItem
            onClick={async (event) => {
              event.stopPropagation();
              await duplicateProject(
                project.id,
                t('dashboard.projects.duplicatedName', { name: project.name }),
              );
              await refetchProjects();
            }}
          >
            {t('dashboard.projects.duplicate')}
          </MenuItem>
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              onOpenModal('deleteProject', { project });
            }}
          >
            {t('dashboard.projects.delete')}
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  );
};
